import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { CT, ymd, todayYmd, rangeLabel } from '../../../lib/dateRange';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

// Two-month range picker for the metrics filter. Built in-repo rather than
// pulling a date-picker dependency: the grid is 20 lines and this way it
// wears the board's own styling.
//
// Grid shape follows ScheduleCalendar's 42-cell month, with one correction:
// that one builds cells from browser-local `new Date`, which would put the
// today-dot on the wrong square (and allow picking a "future" day the server
// rejects) for anyone outside Central. Everything here is CT.

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// The month's cells in 7-wide rows, anchored on the Sunday before the 1st.
// Days outside the month are null: they render as blank squares, not ghost
// numbers (Nic 2026-09-02: a range end sitting on a ghost day drew a second
// black dot on the neighboring month). Trailing all-blank weeks are dropped.
export const monthCells = (y, m) => {
  const first = moment.tz(CT).year(y).month(m).date(1).startOf('day');
  const start = first.clone().subtract(first.day(), 'days');
  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = start.clone().add(i, 'days');
    return d.month() === m ? ymd(d) : null;
  });
  while (cells.length > 7 && cells.slice(-7).every((c) => c === null)) {
    cells.splice(-7, 7);
  }
  return cells;
};

const MonthGrid = ({
  y,
  m,
  draftStart,
  draftEnd,
  hover,
  max,
  since,
  onPick,
  onHover,
}) => {
  const cells = useMemo(() => monthCells(y, m), [y, m]);
  const today = todayYmd();
  const monthLabel = moment.tz(CT).year(y).month(m).format('MMMM YYYY');

  // While only one end is chosen, the hovered day previews the other.
  const provisionalEnd = draftEnd || (draftStart && hover) || null;
  const lo =
    draftStart && provisionalEnd
      ? draftStart <= provisionalEnd
        ? draftStart
        : provisionalEnd
      : draftStart;
  const hi =
    draftStart && provisionalEnd
      ? draftStart <= provisionalEnd
        ? provisionalEnd
        : draftStart
      : draftStart;

  return (
    <div className="w-64">
      <div className="mb-2 text-center text-[13px] font-semibold text-slate-700">
        {monthLabel}
      </div>
      <div className="mb-1 grid grid-cols-7">
        {WEEKDAYS.map((d, i) => (
          <div
            key={`${d}-${i}`}
            className="text-center text-[10px] font-semibold uppercase tracking-wide text-slate-400"
          >
            {d}
          </div>
        ))}
      </div>
      {/* TWO LAYERS, deliberately. The wrapper draws the connecting BAND (a
          rectangle) and the button draws the DAY (always a circle). One
          element doing both meant its shape class flipped between
          rounded-none and rounded-full as the selection moved, which showed
          up as a square blinking into a circle under the cursor.
          No gaps between cells either: a dead pixel row between days makes
          the band flicker as the pointer crosses it. And no transitions —
          hover state changes many times a second while you sweep the grid,
          and animating each one is what made it strobe. */}
      <div className="grid grid-cols-7">
        {cells.map((date, i) => {
          if (!date) return <div key={`blank-${i}`} className="h-8" />;
          const future = date > max;
          const isLo = date === lo;
          const isHi = date === hi;
          const isEnd = isLo || isHi;
          const inBand = lo && hi && date > lo && date < hi;
          // Before the go-live fence there is simply no board data. Muted,
          // but still pickable: "what happened in June" deserves an honest
          // empty answer, not a disabled button.
          const preFence = since && date < since;
          return (
            <div
              key={date}
              onMouseEnter={() => !future && onHover(date)}
              className={[
                'relative',
                inBand || (isEnd && lo !== hi) ? 'bg-slate-100' : '',
                // Round the band's ends so it terminates under the circles
                // instead of squaring off past them.
                isLo && lo !== hi ? 'rounded-l-full' : '',
                isHi && lo !== hi ? 'rounded-r-full' : '',
              ].join(' ')}
            >
              <button
                type="button"
                disabled={future}
                onClick={() => onPick(date)}
                aria-label={date}
                aria-pressed={isEnd}
                className={[
                  'relative flex h-8 w-full items-center justify-center rounded-full text-[12px] font-medium',
                  future ? 'cursor-not-allowed text-slate-300' : '',
                  !future && !isEnd ? 'hover:bg-slate-200' : '',
                  isEnd ? 'bg-slate-900 text-white' : '',
                  !isEnd && preFence && !future ? 'text-slate-400' : '',
                ].join(' ')}
              >
                {Number(date.slice(8, 10))}
                {date === today && !isEnd ? (
                  <span className="absolute inset-x-0 bottom-1 mx-auto h-1 w-1 rounded-full bg-slate-400" />
                ) : null}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Two months side by side need about 560px; below this width the picker
// shows one month at a time (the arrows already step months). Live: it
// follows a resize while the picker is open.
const TWO_MONTHS_QUERY = '(min-width: 640px)';
const useTwoMonths = () => {
  const query = () => window.matchMedia?.(TWO_MONTHS_QUERY);
  const [two, setTwo] = useState(() => query()?.matches ?? true);
  useEffect(() => {
    const mq = query();
    if (!mq) return undefined;
    const on = () => setTwo(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return two;
};

// The calendar itself: no placement of its own (DateRangePicker below puts
// it in a Popover, which keeps it attached and on screen).
const DateRangeCalendar = ({ value, onCommit, max = todayYmd(), since = null }) => {
  const twoMonths = useTwoMonths();
  // DRAFT STATE IS INTERNAL, and only leaves on commit. That is what lets the
  // page's 30s background refetch land mid-selection without closing or
  // resetting the picker.
  const [draftStart, setDraftStart] = useState(value?.start || null);
  const [draftEnd, setDraftEnd] = useState(value?.end || null);
  const [hover, setHover] = useState(null);
  // The LAST month shown: the range's month (or this month), so the month
  // you were last in is the one under the cursor.
  const [view, setView] = useState(() => {
    const anchor = value?.end ? moment.tz(value.end, 'YYYY-MM-DD', CT) : moment.tz(CT);
    return { y: anchor.year(), m: anchor.month() };
  });

  const pick = (date) => {
    if (!draftStart || draftEnd) {
      setDraftStart(date);
      setDraftEnd(null);
      return;
    }
    // A backwards drag is a legitimate gesture, not an error state: swap.
    const [start, end] = date < draftStart ? [date, draftStart] : [draftStart, date];
    setDraftEnd(end);
    setDraftStart(start);
    onCommit({ start, end });
  };

  const navMonth = (dir) => {
    const next = moment.tz(CT).year(view.y).month(view.m).add(dir, 'month');
    setView({ y: next.year(), m: next.month() });
  };

  const last = moment.tz(CT).year(view.y).month(view.m);
  const months = twoMonths ? [last.clone().subtract(1, 'month'), last] : [last];
  const drafting = draftStart && !draftEnd;

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navMonth(-1)}
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Previous month"
        >
          <ChevronLeft size={16} />
        </button>
        <span aria-live="polite" className="text-[12px] text-slate-500">
          {drafting ? 'Pick the end date' : rangeLabel(draftStart, draftEnd) || 'Pick a start date'}
        </span>
        <button
          type="button"
          onClick={() => navMonth(1)}
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Next month"
        >
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="flex gap-4" onMouseLeave={() => setHover(null)}>
        {months.map((mm) => (
          <MonthGrid
            key={mm.format('YYYY-MM')}
            y={mm.year()}
            m={mm.month()}
            draftStart={draftStart}
            draftEnd={draftEnd}
            hover={hover}
            max={max}
            since={since}
            onPick={pick}
            onHover={setHover}
          />
        ))}
      </div>
      {since ? (
        <div className="mt-2 border-t border-slate-100 pt-1.5 text-[11px] text-slate-400">
          Board data starts {since}. Earlier days are empty by design.
        </div>
      ) : null}
    </div>
  );
};

// The picker (Nic 2026-10-25, the professional fix): the calendar in a
// Radix Popover, attached to `children` (the button). The popover keeps it
// on screen through resizes and scrolls, flips above when there's no room
// below, and handles Escape, click-outside and focus. `open` and
// `onOpenChange` are the caller's, so it can close on a commit.
export const DateRangePicker = ({ open, onOpenChange, value, onCommit, max, since, align = 'end', children }) => (
  <Popover open={open} onOpenChange={onOpenChange}>
    <PopoverTrigger asChild>{children}</PopoverTrigger>
    <PopoverContent align={align} aria-label="Pick a date range">
      <DateRangeCalendar
        value={value}
        max={max}
        since={since}
        onCommit={(r) => {
          onOpenChange(false);
          onCommit(r);
        }}
      />
    </PopoverContent>
  </Popover>
);

export default DateRangeCalendar;
